/* InvitationsList — admin invitation tracker with bulk-revoke. */
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Copy, RefreshCw } from 'lucide-react';

const STATUS_VARIANT = {
  pending:  'default',
  accepted: 'secondary',
  revoked:  'destructive',
  expired:  'outline',
};

/* Phase 0.7.2 — admin "Copy invitation link" affordance.
 * The shareable URL is constructed against the current origin so
 * the same UI works across local / preview / production hosts
 * without any extra config. The raw token is NOT rendered to the
 * DOM — only stamped into the clipboard via the user-gesture-
 * triggered async write. */
async function copyInviteLink(inv) {
  if (!inv?.token) {
    toast.error('Token missing — refresh the page and try again.');
    return;
  }
  const url = `${window.location.origin}/invitations/accept?token=${encodeURIComponent(inv.token)}`;
  try {
    await navigator.clipboard.writeText(url);
    toast.success(`Invitation link copied — share it with ${inv.target_email}`);
  } catch {
    toast.error("Couldn't access the clipboard. Long-press to copy the URL manually.");
  }
}

export function InvitationsList({
  invitations, isAdmin, selected, setSelected,
  onRevoke, onReissue, onBulkRevoke, bulkLimit,
}) {
  const toggle = (id) => {
    const n = new Set(selected);
    if (n.has(id)) n.delete(id); else n.add(id);
    setSelected(n);
  };
  const selectedArr = Array.from(selected);

  if (!invitations.length) {
    return (
      <Card className="p-8 text-center text-sm text-slate-300" data-testid="emp-invitations-list-empty">
        No invitations on this account yet.
      </Card>
    );
  }

  return (
    <div className="space-y-3" data-testid="emp-invitations-list">
      {isAdmin && selectedArr.length > 0 && (
        <Card className="flex flex-wrap items-center gap-3 border-amber-400/30 bg-amber-500/10/50 p-3 dark:bg-amber-950/20"
          data-testid="emp-invitations-bulk-bar">
          <span className="text-sm font-medium" data-testid="emp-invitations-bulk-count">
            {selectedArr.length} selected
          </span>
          <span className="text-xs text-slate-300">(cap {bulkLimit})</span>
          <Button size="sm" variant="destructive" className="ml-auto" onClick={onBulkRevoke}
            data-testid="emp-invitations-bulk-revoke">Bulk revoke</Button>
        </Card>
      )}

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-slate-300">
            <tr>
              {isAdmin && <th className="w-8 p-2"></th>}
              <th className="p-2">Email</th>
              <th className="p-2">Role</th>
              <th className="p-2">Status</th>
              <th className="p-2">Issued</th>
              <th className="p-2">Expires</th>
              {isAdmin && <th className="p-2 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {invitations.map((inv) => {
              const isPending = inv.status === 'pending';
              return (
                <tr key={inv.id} className="border-t border-zinc-200 dark:border-zinc-800"
                  data-testid={`emp-invitations-row-${inv.id}`}>
                  {isAdmin && (
                    <td className="p-2">
                      <Checkbox
                        checked={selected.has(inv.id)}
                        onCheckedChange={() => toggle(inv.id)}
                        disabled={!isPending}
                        data-testid={`emp-invitations-select-${inv.id}`}
                      />
                    </td>
                  )}
                  <td className="p-2">{inv.target_email}</td>
                  <td className="p-2">
                    <Badge variant="outline">{inv.target_role}</Badge>
                  </td>
                  <td className="p-2">
                    <Badge variant={STATUS_VARIANT[inv.status] || 'outline'}
                      data-testid={`emp-invitations-status-${inv.id}`}>
                      {inv.status}
                    </Badge>
                  </td>
                  <td className="p-2 text-xs text-slate-300">
                    {inv.created_at ? new Date(inv.created_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="p-2 text-xs text-slate-300">
                    {inv.expires_at ? new Date(inv.expires_at).toLocaleDateString() : '—'}
                  </td>
                  {isAdmin && (
                    <td className="p-2 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button size="sm" variant="ghost"
                          disabled={!isPending}
                          onClick={() => copyInviteLink(inv)}
                          className="text-slate-200 hover:text-ndor-ink dark:text-zinc-300 dark:hover:text-zinc-100"
                          data-testid={`emp-invitations-copy-link-${inv.id}`}
                          aria-label={`Copy invitation link for ${inv.target_email}`}>
                          <Copy className="h-3.5 w-3.5 sm:mr-1.5" />
                          <span className="hidden sm:inline">Copy link</span>
                        </Button>
                        {onReissue && (
                          <Button size="sm" variant="ghost"
                            disabled={!isPending}
                            onClick={() => onReissue(inv)}
                            className="text-slate-200 hover:text-ndor-ink dark:text-zinc-300 dark:hover:text-zinc-100"
                            data-testid={`emp-invitations-reissue-${inv.id}`}
                            aria-label={`Reissue invitation for ${inv.target_email} (revokes the current invitation and issues a fresh one)`}
                            title="Revoke this invitation and issue a fresh one with a new link.">
                            <RefreshCw className="h-3.5 w-3.5 sm:mr-1.5" />
                            <span className="hidden sm:inline">Reissue</span>
                          </Button>
                        )}
                        <Button size="sm" variant="ghost"
                          disabled={!isPending}
                          onClick={() => onRevoke(inv.id)}
                          className="text-rose-300 hover:text-rose-200"
                          data-testid={`emp-invitations-revoke-${inv.id}`}>
                          Revoke
                        </Button>
                      </div>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
